<template>
    <Dialog :visible="showDialog" class="action-popup email-update-done-popup" :modal="true" :draggable="false">
        <template #container>

            <div class="dialog-header flex flex-row">
                <div style="flex: 1;"></div>
                <div style="flex: 5;" class="flex flex-row align-items-center justify-content-center">Verify Your New Email Address!</div>
                <div style="flex: 1;" class="flex flex-row align-items-center justify-content-end">
                    <SvgCross class="cursor-pointer" className="table-licenses-svg" color="var(--button-normal-color)" @click="close()"/>
                </div>
            </div>
            <div class="dialog-content align-items-center text-center pt-4" style="padding-bottom: 1.2rem;">
                <span class="text sm:px-5 px-1">An email update confirmation link has been sent to <span style="font-weight: var(--font-weight-bold);">{{ requestContent.email }}</span>.</span>
                <span class="text mb-3 sm:px-5 px-1">After confirmation, you must refresh profile informations to see the actual update.</span>
                <span class="text"> Please check your inbox.</span>
            </div>
            <div class="dialog-footer flex flex-column mt-0 pt-0 pb-4 text-center">
                <span class="text mb-4">Did not receive the email? Check your spams or wait a few minutes.</span>
                <span class="text px-2 sm:px-8 text-center mb-1" style="font-style: italic; color: var(--inputcomponent-invalid-color);" v-if="emailSendingLimitText">{{ emailSendingLimitText }}</span>
                <span class="text mb-1" style="font-style: italic; color: var(--mysuccess-color);" v-if="emailSentAgainText" >{{ emailSentAgainText }}</span>

                <div v-if="!httpEditProfileLoading">
                    <span id="send-again-text" class="text cursor-pointer" @click="sendAgain">Send email again</span>
                </div>
                <div v-else>
                    <span class="text" style="opacity: 0.65;">Wait ...</span>
                </div>
            </div>

        </template>
    </Dialog>
</template>

<script setup>
// --- IMPORT ---
import { ref, toRefs, computed } from 'vue';
// PrimeVue
import Dialog from 'primevue/dialog';
// vue
import SvgCross from '@/components/SVGs/SvgCross.vue';
// Others
import InputsUtils from '@/composables/InputsUtils'
import toastService from '@/composables/toastService';
import { useStore } from 'vuex';

const store = useStore()
const user = computed(() => store.state.user.user)

// API
import useApiRequest from '@/composables/HTTPS/useApiRequest';
const { fetchEditProfile } = useApiRequest()
const httpEditProfileLoading = ref(false)
const httpEditProfileData = ref(null)

// --- EMITS ---
const emit = defineEmits(['close'])

// --- PROPS ---
const props = defineProps({
    showDialog: Boolean,
    requestContent: Object
})
const { showDialog, requestContent } = toRefs(props);

// --- Close ---
const close = () => {
    emailSentAgainText.value = null
    emailSendingLimitText.value = null
    emit('close');
}

// --- SEND EMAIL AGAIN ---
const emailSentAgainText = ref(null)
const emailSendingLimitText = ref(null)
const sendAgain = async () => {
    // Send HTTP request
    httpEditProfileLoading.value = true
    try {
        httpEditProfileData.value = await fetchEditProfile({email: requestContent.value.email ?? user.value.email, password_plain_auth: requestContent.value.password_plain_auth ?? ""})
        emailSendingLimitText.value = null
        emailSentAgainText.value = "A new confirmation email has been sent."
    } catch (err) {
        handleEditProfileError(err)
    } finally {
        httpEditProfileLoading.value = false
    }
}
// Handle an error from the server
const handleEditProfileError = (error) => {
    try {
        if (error.response == null) { // If there is no .response key, then it means it is not an error from the server, it did not reach it
            toastService.add({ severity: 'error', summary: `Edit Profile: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
            return;
        }
        const errorStatus = error.response.status
        const errorCode = error.response.data.error_code
        const errorMessage = error.response.data.error_message
        switch (errorStatus) {
            case 400: // ACCESS_TOKEN_MISSING / MISSING_OBJ / MISSING_OBJ_ELEM (400)
                if (errorCode == 'ACCESS_TOKEN_MISSING') {
                    toastService.add({ severity: 'error', summary: "Edit Profile: Authorization Issue.", detail: 'You are not logged in.', life: 5000 });
                    // Logout
                    logoutUser()
                }
                else if (errorCode == 'MISSING_OBJ' || errorCode == 'MISSING_OBJ_ELEM') { // MISSING_OBJ / MISSING_OBJ_ELEM
                    toastService.add({ severity: 'warn', summary: "Edit Profile: Missing Information.", detail: 'It seems that the request body does not contain the necessary information.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 401: // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID / EMAIL_NOT_FREE / WRONG_PASSWORD (401)
                if (errorCode == 'WRONG_PASSWORD') {
                    toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 })
                    close()
                }
                else if (errorCode == 'EMAIL_NOT_FREE') {
                    toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 })
                    close()
                }
                else if (errorCode == 'ACCESS_TOKEN_EXPIRED' || errorCode == 'ACCESS_TOKEN_INVALID') { // ACCESS_TOKEN_EXPIRED / ACCESS_TOKEN_INVALID : we arrive here only if the token is still invalid AFTER the refresh => logout
                    // Logout: Do nothing, the interceptor should have made a refresh token request!
                    // logoutUser()
                }
                else if (errorCode == 'EMAIL_SENDING_LIMIT_REACHED') {
                    emailSentAgainText.value = null
                    emailSendingLimitText.value = "The email update confirmation link has been sent very recently. Please wait a few moment before trying to send the email again."
                }
                else { toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 404: // Account not found: account has disappear : ACCOUNT_NOT_FOUND (404) => toast + logout
                if (errorCode == 'ACCOUNT_NOT_FOUND') {
                    toastService.add({ severity: 'error', summary: "Edit Profile: Account Not Found.", detail: 'The account no more exists.', life: 5000 });
                    close()
                    // Logout
                    logoutUser()
                }
                else { toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            case 500: // DATABASE_ERROR (500) => toast
                if (errorCode == 'DATABASE_ERROR') {
                    toastService.add({ severity: 'error', summary: "Edit Profile: Internal Error.", detail: 'Sorry, it seems that there is an internal error. Try later.', life: 5000 });
                }
                else { toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 }); }
                break;
            default:
                toastService.add({ severity: 'error', summary: `Edit Profile: Error ${errorStatus}.`, detail: errorMessage, life: 12000 });
                break;
        }
    } catch {
        toastService.add({ severity: 'error', summary: `Edit Profile: Server not reachable.`, detail: `The server is not reachable. Please make sure you are connected to the internet. If you are, please try again.`, life: 5000 });
    }
}
</script>

<style scoped>
/* Forgot password */
#send-again-text {
    text-decoration: none;
    color: var(--button-normal-color);
    border-bottom: 1px solid transparent;
}
#send-again-text:hover {
    border-bottom: 1px solid var(--button-normal-color);
}
</style>